// src/utils/errorHelpers.ts
// Helper functions para manejar errores de la API

import axios from 'axios';
import { ApiError } from '../api/core/ApiError';

const GENERIC_HTTP_ERROR_MESSAGES = new Set([
  'Bad Request',
  'Unauthorized',
  'Forbidden',
  'Not Found',
  'Internal Server Error',
  'Bad Gateway',
  'Service Unavailable',
]);

function messageFromApiBody(data: unknown): string | null {
  if (typeof data === 'string' && data.trim() !== '') {
    return data;
  }
  if (data == null || typeof data !== 'object') {
    return null;
  }
  const raw = (data as { message?: unknown }).message;
  if (typeof raw === 'string' && raw.trim() !== '') {
    return raw;
  }
  if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === 'string') {
    return raw.join(' ');
  }
  if (raw != null && typeof raw === 'object' && 'message' in raw) {
    const inner = (raw as { message: unknown }).message;
    if (typeof inner === 'string' && inner.trim() !== '') {
      return inner;
    }
  }
  if ('errors' in data && typeof (data as { errors: unknown }).errors === 'object') {
    const errors = (data as { errors: Record<string, string[]> }).errors;
    const errorMessages = Object.values(errors).flat();
    if (errorMessages.length > 0) {
      return errorMessages.join(', ');
    }
  }
  if ('error' in data && typeof (data as { error: unknown }).error === 'string') {
    return (data as { error: string }).error;
  }
  return null;
}

/** Response body from ApiError or Axios failed requests. */
export function getApiResponseBody(error: unknown): unknown | null {
  if (error instanceof ApiError) {
    return error.body ?? null;
  }
  if (axios.isAxiosError(error)) {
    return error.response?.data ?? null;
  }
  return null;
}

/** Machine-readable error code from API body (when present). */
export function extractErrorCode(error: unknown): string | null {
  const data = getApiResponseBody(error);
  if (data == null || typeof data !== 'object') {
    return null;
  }
  const code = (data as { code?: unknown }).code;
  return typeof code === 'string' && code.trim() !== '' ? code : null;
}

const PHYSICAL_COUNT_PREVIEW_ERROR_MESSAGES: Record<string, string> = {
  PHC_INVALID_FILE_STRUCTURE:
    'Formato incorrecto. El archivo debe tener un código por fila en una sola columna, sin encabezados ni columnas extra.',
  PHC_INVALID_FILE_TYPE: 'Solo se admiten archivos CSV o XLSX.',
  PHC_EMPTY_FILE: 'El archivo está vacío o no contiene códigos válidos.',
  PHC_FILE_TOO_LARGE: 'El archivo supera el tamaño máximo permitido.',
  PHC_TOO_MANY_CODES: 'El archivo contiene demasiados códigos.',
  PHC_INVALID_CODE_LENGTH: 'Uno o más códigos superan la longitud máxima permitida.',
};

export function extractPhysicalCountPreviewErrorMessage(error: unknown): string {
  const code = extractErrorCode(error);
  if (code != null && PHYSICAL_COUNT_PREVIEW_ERROR_MESSAGES[code] != null) {
    return PHYSICAL_COUNT_PREVIEW_ERROR_MESSAGES[code];
  }
  return extractErrorMessage(
    error,
    'No se pudo validar el archivo. Verifique el formato vertical.',
  );
}

/**
 * Maps known sale/payment/inventory concurrency backend messages to Spanish.
 * Voice: docs/voice.md — impersonal facts + usted for actions.
 * Keep patterns tight: broad matches mislabel unrelated domain errors.
 */
function mapSalesConcurrencyErrorMessage(message: string): string {
  if (/is not available to transfer|not available for transfer/i.test(message)) {
    return 'Esta pieza ya no está disponible para transferir.';
  }
  if (/Transfer is already (completed|cancelled|canceled)/i.test(message)) {
    return 'Esta transferencia ya fue procesada.';
  }
  if (
    /cannot be reserved|not available to reserve|is not available.*reserv|no está disponible para bloquear|already reserved/i.test(
      message,
    )
  ) {
    return 'Esta pieza ya no se puede reservar.';
  }
  if (
    /Product .+ is not available|Warehouse inventory product .+ is not available|is not available/i.test(
      message,
    )
  ) {
    return 'Esta pieza ya no está disponible. Actualice el carrito e intente de nuevo.';
  }
  if (/exceeds remaining balance/i.test(message)) {
    return 'El saldo pendiente ya fue cubierto.';
  }
  if (/Cannot process payment for order with status/i.test(message)) {
    return 'No se puede cobrar: la venta ya está cerrada.';
  }
  return message;
}

/**
 * Extrae el mensaje de error de una respuesta de la API
 * @param error - Error capturado (puede ser ApiError u otro tipo)
 * @param defaultMessage - Mensaje por defecto si no se puede extraer
 * @returns Mensaje de error legible
 */
export function extractErrorMessage(
  error: unknown,
  defaultMessage: string = 'No se pudo completar la acción. Intente de nuevo.',
): string {
  const body = getApiResponseBody(error);
  if (body != null) {
    const fromBody = messageFromApiBody(body);
    if (fromBody != null) {
      return mapSalesConcurrencyErrorMessage(fromBody);
    }
  }

  // Si es un ApiError
  if (error instanceof ApiError) {
    if (error.message) {
      const mapped = mapSalesConcurrencyErrorMessage(error.message);
      if (mapped && mapped.trim() !== '' && !GENERIC_HTTP_ERROR_MESSAGES.has(error.message)) {
        return mapped;
      }
    }
    switch (error.status) {
      case 400:
        return 'Datos inválidos. Verifique la información ingresada.';
      case 401:
        return 'No autorizado. Inicie sesión de nuevo.';
      case 403:
        return 'No tiene permisos para realizar esta acción.';
      case 404:
        return 'Recurso no encontrado.';
      case 409:
        return 'El recurso ya existe o está en uso.';
      case 500:
        return 'Error del servidor. Intente de nuevo más tarde.';
      default:
        return defaultMessage;
    }
  }

  // Si es un Error estándar
  if (error instanceof Error) {
    const message = error.message;
    if (
      message &&
      message.trim() !== '' &&
      !message.startsWith('Request failed with status code') &&
      !GENERIC_HTTP_ERROR_MESSAGES.has(message)
    ) {
      return mapSalesConcurrencyErrorMessage(message);
    }
  }

  // Si es un objeto con mensaje
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message: unknown }).message;
    if (typeof message === 'string' && message.trim() !== '') {
      return message;
    }
  }

  return defaultMessage;
}

/**
 * Extrae errores de validación por campo
 * @param error - Error capturado
 * @returns Objeto con errores por campo, o null si no hay errores de validación
 */
export function extractValidationErrors(error: unknown): Record<string, string[]> | null {
  if (error instanceof ApiError && error.body) {
    if (typeof error.body === 'object' && error.body !== null && 'errors' in error.body) {
      const errors = error.body.errors;
      if (typeof errors === 'object' && errors !== null) {
        return errors as Record<string, string[]>;
      }
    }
  }
  return null;
}

/**
 * Mapea errores de validación del backend a campos del formulario
 * Convierte nombres de campos del backend a nombres del frontend si es necesario
 * @param validationErrors - Errores de validación del backend
 * @returns Errores mapeados a campos del formulario
 */
export function mapValidationErrorsToFormFields(
  validationErrors: Record<string, string[]>,
): Record<string, string> {
  const mappedErrors: Record<string, string> = {};

  // Mapeo de campos del backend a campos del frontend
  const fieldMapping: Record<string, string> = {
    email: 'email',
    password: 'password',
    firstName: 'firstName',
    lastName: 'lastName',
    phone: 'phone',
    roleIds: 'roleIds',
    isActive: 'isActive',
  };

  for (const [field, messages] of Object.entries(validationErrors)) {
    // Mapear el nombre del campo si es necesario
    const formField = fieldMapping[field] || field;
    // Tomar el primer mensaje de error (puede haber múltiples)
    mappedErrors[formField] = Array.isArray(messages) ? messages[0] : messages;
  }

  return mappedErrors;
}

/**
 * Detecta si un error es de email duplicado
 * @param error - Error capturado
 * @returns true si es un error de email duplicado
 */
export function isDuplicateEmailError(error: unknown): boolean {
  if (error instanceof ApiError) {
    // Código 409 generalmente indica conflicto (duplicado)
    if (error.status === 409) {
      return true;
    }
    // También verificar en el mensaje
    const message = extractErrorMessage(error, '').toLowerCase();
    if (
      message.includes('email') &&
      (message.includes('duplicate') ||
        message.includes('already exists') ||
        message.includes('ya existe') ||
        message.includes('en uso'))
    ) {
      return true;
    }
  }
  return false;
}
